import * as React from "react";
import CreateTeamNameAndUse from "./create-team-name-and-use";
import CompanyFollowUpQuestions from "./company-followup-questions";
import InviteMembers from "./invite-members";
import TOSAndComplete from "./tos-and-complete";
import { getBuilderUserDatabase } from "lib/web-database";
import { getCurrentTermsOfService } from "common/terms-of-service";
import { assertNever, dontAwait } from "common/Support";
import { OnboardingInfo } from "./onboardingInfo";
import { getAppFacilities } from "webapp/lib/app-renderer";
import { logError } from "common/debug-print";
import { trackCompletedSignUpFlow, trackEvent } from "analytics";
import { useBuilderSelector } from "hooks/use-builder-selector";
import { isUserData } from "common/Database";

interface Props {
    orgID: string;
    userID: string;
    onDone: () => void;
}

type IndividualTrack = "createTeamNameAndUse" | "tosAndComplete";

type WorkTrack = "createTeamNameAndUse" | "companyFollowUpQuestions" | "inviteMembers" | "tosAndComplete";

type Stage = IndividualTrack | WorkTrack;

const OnBoarding: React.FunctionComponent<Props> = ({ orgID, onDone, userID }) => {
    const userData = useBuilderSelector(state => state.userData);
    const [stage, setStage] = React.useState<Stage>("createTeamNameAndUse");
    const [termsLink, setTermsLink] = React.useState<string | undefined>(undefined);
    const [privacyLink, setPrivacyLink] = React.useState<string | undefined>(undefined);
    const [onboardingInfo, setOnboardingInfo] = React.useState<OnboardingInfo>({});

    React.useEffect(() => {
        const fn = async () => {
            const result = await getCurrentTermsOfService(getBuilderUserDatabase(userID));
            setTermsLink(result?.termsLink);
            setPrivacyLink(result?.privacyLink);
        };
        dontAwait(fn());
    }, [userID]);

    const updateOnboardingInfo = React.useCallback((updates: Partial<OnboardingInfo>): void => {
        setOnboardingInfo(info => ({ ...info, ...updates }));
    }, []);

    const updateContactAndCloseModal = React.useCallback(async () => {
        await createOrUpdateHubspotContact(onboardingInfo);

        if (isUserData(userData) && userData.email !== undefined) {
            await trackCompletedSignUpFlow(userData.email);
        } else {
            logError("Unexpected invalid user data or empty email.");
        }

        trackEvent("completed sign up flow", { isForWork: onboardingInfo.isForWork ?? false });
        onDone();
    }, [onDone, onboardingInfo, userData]);

    switch (stage) {
        case "createTeamNameAndUse":
            return (
                <CreateTeamNameAndUse
                    orgID={orgID}
                    updateOnboardingInfo={updateOnboardingInfo}
                    onDone={(isForWork: boolean) => setStage(isForWork ? "companyFollowUpQuestions" : "tosAndComplete")}
                />
            );
        case "companyFollowUpQuestions":
            return (
                <CompanyFollowUpQuestions
                    orgID={orgID}
                    updateOnboardingInfo={updateOnboardingInfo}
                    onDone={() => setStage("inviteMembers")}
                />
            );
        case "inviteMembers":
            return <InviteMembers orgID={orgID} onDone={() => setStage("tosAndComplete")} source="onboarding" />;
        case "tosAndComplete":
            return (
                <TOSAndComplete
                    orgID={orgID}
                    termsLink={termsLink}
                    privacyLink={privacyLink}
                    onDone={updateContactAndCloseModal}
                />
            );
        default:
            assertNever(stage);
    }
};

async function createOrUpdateHubspotContact(onboardingInfo: OnboardingInfo): Promise<void> {
    try {
        await getAppFacilities().callAuthCloudFunction("createOrUpdateHubspotContact", onboardingInfo);
    } catch (err: unknown) {
        logError("Could not create/update hubspot contact", err);
        return;
    }
}

export default OnBoarding;
